import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import { toast } from 'react-toastify';

import api from '../../services/api';
import { DetailField, LoadingState, PageHeader, PageLayout, SectionCard } from '../../components/ui';

const leaveTypes = [
  'Annual',
  'Sick',
  'Emergency',
  'Unpaid',
  'BabyBorn',
  'FamilyDeath',
  'Exam',
  'Haj',
  'Marriage',
  'Pregnancy',
  'HusbandDeath',
  'Payroll',
];

const initialBalanceData = {
  user_id: '',
  name: '',
  leave_type: '',
  balance: 0,
};

function FieldGroup({ icon, title, description, children }) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
        {icon}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {children}
    </Box>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ color: 'text.secondary', display: 'grid', placeItems: 'center', mt: 0.1 }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 850, overflowWrap: 'anywhere' }}>
          {value || '-'}
        </Typography>
      </Box>
    </Stack>
  );
}

function EditVacationBalancePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [balanceData, setBalanceData] = useState(initialBalanceData);
  const [originalBalanceData, setOriginalBalanceData] = useState(initialBalanceData);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const isAnnual = balanceData.leave_type === 'Annual';
  const balanceValue = Number(balanceData.balance);

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.hr?.radius?.md || 2,
      backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.74 : 0.92),
    },
  };

  const completionPct = useMemo(() => {
    const checks = [
      Boolean(balanceData.user_id),
      Boolean(balanceData.leave_type),
      Number.isFinite(balanceValue) && balanceValue >= 0,
    ];
    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [balanceData.leave_type, balanceData.user_id, balanceValue]);

  const fetchBalance = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get(`/admin/vacation-balances/${id}`);
      const nextBalanceData = {
        user_id: String(res.data.user_id || ''),
        name: res.data?.user?.name || '',
        leave_type: res.data.leave_type || '',
        balance: res.data.balance ?? 0,
      };
      setBalanceData(nextBalanceData);
      setOriginalBalanceData(nextBalanceData);
      setReason('');
    } catch (err) {
      console.error('Failed to fetch balance:', err);
      setError('Failed to fetch balance.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  const handleReset = () => {
    setError('');
    setBalanceData(originalBalanceData);
    setReason('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!isAnnual) {
      toast.error('Only Annual leave balances can be edited.');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/admin/vacation-balances/${id}`, {
        user_id: Number(balanceData.user_id),
        leave_type: balanceData.leave_type,
        balance: Number(balanceData.balance),
        reason: reason || undefined,
      });

      toast.success('Vacation balance updated successfully!');
      navigate('/admin/vacation-balances');
    } catch (err) {
      console.error('Failed to update vacation balance:', err);
      setError(err.response?.data?.message || 'Error updating balance.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout backgroundAccent compact maxWidth="xl">
      <PageHeader
        compact
        variant="dashboard"
        overline="Vacation balances"
        title={`Edit Vacation Balance #${id}`}
        subtitle="Adjust annual balance records and capture a reason for audit history."
        actions={[
          <Button
            key="back"
            variant="outlined"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate('/admin/vacation-balances')}
          >
            Back to Balances
          </Button>,
        ]}
      />

      {loading ? (
        <LoadingState rows={8} />
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' },
            gap: 1.5,
            alignItems: 'start',
          }}
        >
          <SectionCard
            tone="dashboard"
            title="Balance Details"
            subheader="Only Annual leave balance records are editable."
            contentSx={{ p: { xs: 1.35, md: 1.7 } }}
          >
            <Stack spacing={1.8}>
              {error ? <Alert severity="error">{error}</Alert> : null}
              {!isAnnual ? (
                <Alert severity="warning">
                  Only Annual leave types can be edited. This record is {balanceData.leave_type || 'unknown'}, so fields are read-only.
                </Alert>
              ) : null}

              <FieldGroup
                icon={<PersonOutlineRoundedIcon color="primary" />}
                title="Employee"
                description="Employee is locked for existing balance records."
              >
                <TextField
                  label="User Name"
                  value={balanceData.name}
                  disabled
                  sx={fieldSx}
                />
              </FieldGroup>

              <Divider />

              <FieldGroup
                icon={<CalendarMonthRoundedIcon color="primary" />}
                title="Leave Type and Balance"
                description="Edit the annual balance in days."
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1.2,
                  }}
                >
                  <TextField
                    select
                    label="Leave Type"
                    disabled
                    value={balanceData.leave_type}
                    onChange={(event) => setBalanceData((prev) => ({ ...prev, leave_type: event.target.value }))}
                    required
                    sx={fieldSx}
                  >
                    {leaveTypes.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    type="number"
                    label="Balance"
                    value={balanceData.balance}
                    onChange={(event) => setBalanceData((prev) => ({ ...prev, balance: event.target.value }))}
                    inputProps={{ min: 0, step: 'any' }}
                    required
                    disabled={!isAnnual}
                    sx={fieldSx}
                  />
                </Box>
              </FieldGroup>

              <Divider />

              <FieldGroup
                icon={<NotesRoundedIcon color="primary" />}
                title="Audit Reason"
                description="Reason is saved with the balance history when provided."
              >
                <TextField
                  label="Reason for Update"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  multiline
                  minRows={3}
                  placeholder="e.g. annual increment, correction, migration adjustment"
                  disabled={!isAnnual}
                  sx={fieldSx}
                />
              </FieldGroup>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<RestartAltRoundedIcon />}
                  onClick={handleReset}
                  disabled={saving}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveRoundedIcon />}
                  disabled={saving || !isAnnual}
                >
                  {saving ? 'Saving...' : 'Update Balance'}
                </Button>
              </Stack>
            </Stack>
          </SectionCard>

          <Stack spacing={1.5} sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
            <SectionCard
              tone="dashboard"
              title="Balance Summary"
              subheader="Current edit state."
              contentSx={{ p: 1.6 }}
            >
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    color={completionPct === 100 ? 'success' : 'warning'}
                    label={`${completionPct}% complete`}
                  />
                  <Chip size="small" variant="outlined" label={isAnnual ? 'Editable' : 'Read only'} />
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={completionPct}
                  sx={{ height: 8, borderRadius: 999, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                />
                <SummaryRow
                  icon={<PersonOutlineRoundedIcon fontSize="small" />}
                  label="Employee"
                  value={balanceData.name}
                />
                <SummaryRow
                  icon={<CalendarMonthRoundedIcon fontSize="small" />}
                  label="Leave Type"
                  value={balanceData.leave_type}
                />
                <DetailField
                  compact
                  label="Balance"
                  value={`${Number.isFinite(balanceValue) ? balanceValue : 0} day(s)`}
                  icon={<TrendingUpRoundedIcon fontSize="small" />}
                />
                <DetailField
                  compact
                  label="Reason"
                  value={reason || 'No reason entered'}
                  icon={<NotesRoundedIcon fontSize="small" />}
                />
              </Stack>
            </SectionCard>
          </Stack>
        </Box>
      )}
    </PageLayout>
  );
}

export default EditVacationBalancePage;
